/**
 * ============================================================
 * useNotifications.ts — Sistema de Notificacoes em Tempo Real
 * ============================================================
 *
 * Responsabilidade: Gerenciar notificacoes do usuario — fetch inicial,
 *   escuta realtime via Supabase, push notifications nativas,
 *   e acoes de marcar como lida/limpar.
 *
 * Retorno:
 *   - notifications: Notification[] — lista de notificacoes (max 50)
 *   - unreadCount: number — contador de nao lidas
 *   - markAsRead/markAllAsRead/clearAll — acoes de gestao
 *   - pushEnabled/enablePush — controle de push notifications
 *   - refetch — forca recarga das notificacoes
 *
 * Efeitos colaterais:
 *   - Registra Service Worker para push notifications
 *   - Assina canal Realtime do Supabase para INSERT em notifications
 *   - Toca som "plim" e mostra browser notification ao receber nova
 *   - Delay de 1s na assinatura para evitar warning do StrictMode
 *
 * Quando usar: No componente de sino/badge de notificacoes.
 * ============================================================
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStore } from '@/store/useStore';
import { toast } from 'sonner';
import { playPlim } from '@/lib/sounds';
import { safeFire } from '@/lib/safeMutate';

/** Estrutura de uma notificacao do sistema */
export interface Notification {
  id: string;
  user_id: string;
  project_id: string;
  type: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  is_read: boolean;
  created_at: string;
}

// Service Worker registration for push
async function registerPushSW(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return false;
  try {
    await navigator.serviceWorker.register('/sw-push.js', { scope: '/' });
    return true;
  } catch { return false; }
}

// Browser Push Notification helpers
async function requestPushPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') { await registerPushSW(); return true; }
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  if (result === 'granted') await registerPushSW();
  return result === 'granted';
}

function showBrowserNotification(title: string, body?: string) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  // Try SW notification first for background support
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(reg => {
      reg.showNotification(title, {
        body: body || undefined,
        icon: '/pwa-icon-512.png',
        badge: '/pwa-icon-512.png',
        tag: 'legacy-crm-' + Date.now(),
      });
    }).catch(() => {
      // Fallback to regular notification
      try {
        const notif = new window.Notification(title, { body: body || undefined, icon: '/pwa-icon-512.png' });
        notif.onclick = () => { window.focus(); notif.close(); };
        setTimeout(() => notif.close(), 8000);
      } catch { /* empty */ }
    });
    return;
  }
  try {
    const notif = new window.Notification(title, {
      body: body || undefined,
      icon: '/pwa-icon-512.png',
      badge: '/pwa-icon-512.png',
      tag: 'legacy-crm-' + Date.now(),
      requireInteraction: false,
    });
    notif.onclick = () => { window.focus(); notif.close(); };
    setTimeout(() => notif.close(), 8000);
  } catch { /* empty */ }
}

export function useNotifications() {
  const { activeProjectId } = useStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);

  // Request push permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  const enablePush = useCallback(async () => {
    if (!('Notification' in window)) {
      toast.error('Seu navegador não suporta notificações push.');
      return false;
    }
    if (Notification.permission === 'denied') {
      toast.error('Notificações foram bloqueadas. Ative nas configurações do navegador para receber alertas.');
      return false;
    }
    const granted = await requestPushPermission();
    setPushEnabled(granted);
    if (!granted) {
      toast.warning('Permissão de notificações não foi concedida. Você pode ativar depois nas configurações do navegador.');
    } else {
      toast.success('Notificações push ativadas!');
    }
    return granted;
  }, []);

  const fetchNotifications = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;

    // Hide stale notifications (>7d old AND already read). They clutter the
    // popover without offering value — e.g. "Novo lead criado há 35 dias".
    // Unread items always show regardless of age so we never silently lose
    // things the user hasn't seen.
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .or(`is_read.eq.false,created_at.gte.${sevenDaysAgo}`)
      .order('created_at', { ascending: false })
      .limit(50);

    if (data) {
      setNotifications(data as Notification[]);
      setUnreadCount(data.filter((n: any) => !n.is_read).length);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchNotifications();

    // Delay subscription to avoid WebSocket close warning during React StrictMode double-mount
    let channel: ReturnType<typeof supabase.channel> | null = null;
    const timer = setTimeout(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      const userId = session?.user?.id || null;
      if (!userId) return;

      channel = supabase
        .channel(`notifications-${activeProjectId || 'global'}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
          (payload) => {
            const newNotif = payload.new as Notification;
            setNotifications(prev => [newNotif, ...prev].slice(0, 50));
            setUnreadCount(prev => prev + 1);
            try { playPlim(0.3); } catch { /* empty */ }
            showBrowserNotification(newNotif.title, newNotif.body || undefined);
          }
        )
        .subscribe();
    }, 1000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (channel) supabase.removeChannel(channel);
    };
  }, [activeProjectId]); // Re-subscribe when project changes

  const markAsRead = useCallback(async (id: string) => {
    const ok = await safeFire(() => supabase.from('notifications').update({ is_read: true }).eq('id', id), 'Erro ao marcar como lida');
    if (!ok) return;
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession(); const user = session?.user;
    if (!user) return;
    const ok = await safeFire(() => supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id).eq('is_read', false), 'Erro ao marcar todas como lidas');
    if (!ok) return;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, []);

  const clearAll = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession(); const user = session?.user;
    if (!user) return;
    const ok = await safeFire(() => supabase.from('notifications').delete().eq('user_id', user.id), 'Erro ao limpar notificações');
    if (!ok) return;
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return { notifications, unreadCount, markAsRead, markAllAsRead, clearAll, refetch: fetchNotifications, pushEnabled, enablePush };
}
